import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/utils";
import { IconButton } from "./icon-button";

/* ——— Pilha de camadas modais: Esc/foco só valem para a do topo ——— */
const stack: string[] = [];
let scrollLocks = 0;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useModalLayer(open: boolean, panelRef: React.RefObject<HTMLElement | null>, onEscape: (() => void) | undefined) {
  const id = useId();
  const escRef = useRef(onEscape);
  useEffect(() => {
    escRef.current = onEscape;
  });

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    stack.push(id);
    if (scrollLocks++ === 0) document.body.style.overflow = "hidden";

    // Foco inicial: [data-autofocus] → primeiro campo → painel.
    const t = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      const target =
        panel.querySelector<HTMLElement>("[data-autofocus], [autofocus]") ??
        panel.querySelector<HTMLElement>("input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled])") ??
        panel;
      target.focus();
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== id) return;
      if (e.key === "Escape" && escRef.current) {
        e.preventDefault();
        escRef.current();
      } else if (e.key === "Tab" && panelRef.current) {
        const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      const i = stack.lastIndexOf(id);
      if (i >= 0) stack.splice(i, 1);
      if (--scrollLocks === 0) document.body.style.overflow = "";
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, [open, id, panelRef]);
}

export type DialogSize = "sm" | "md" | "lg" | "xl" | "full";

const sizeClass: Record<DialogSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  full: "sm:max-w-[min(96vw,1400px)]",
};

type DialogProps = {
  open: boolean;
  title: string;
  /** Linha de identificação em fonte mono (login, id, ramal). */
  subtitle?: string;
  /** Descrição em texto corrido abaixo do título. */
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Ações no rodapé, da menos para a mais importante (a primária por último, à direita). */
  footer?: ReactNode;
  size?: DialogSize;
  /** Se false, não fecha ao clicar fora nem com Esc (ex.: revelação de senha, operação em andamento). */
  closeOnBackdrop?: boolean;
  className?: string;
};

/**
 * Diálogo modal acessível: role="dialog", aria-modal, foco preso, Esc fecha,
 * foco volta ao gatilho, rolagem do corpo travada. No mobile vira folha inferior.
 */
export function Dialog({ open, title, subtitle, description, onClose, children, footer, size = "md", closeOnBackdrop = true, className }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useModalLayer(open, panelRef, closeOnBackdrop ? onClose : undefined);
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-overlay animate-fade-in sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          "flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-surface-raised text-fg shadow-lg outline-none animate-pop-in sm:max-h-[88vh] sm:rounded-2xl",
          sizeClass[size],
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-title text-fg">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 truncate font-mono text-body-sm text-fg-subtle">{subtitle}</p> : null}
            {description ? (
              <p id={descId} className="mt-1 text-body-sm text-fg-subtle">
                {description}
              </p>
            ) : null}
          </div>
          {closeOnBackdrop ? <IconButton icon={X} label="Fechar" size="sm" onClick={onClose} tooltip={false} className="-mr-1.5 -mt-0.5" /> : null}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-body text-fg">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-surface px-5 py-3">{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

const drawerSize = { sm: "sm:w-[24rem]", md: "sm:w-[32rem]", lg: "sm:w-[44rem]" } as const;

type DrawerProps = Omit<DialogProps, "size"> & { size?: keyof typeof drawerSize };

/** Painel lateral (detalhes de registro, filtros avançados). Mesmo contrato do Dialog. */
export function Drawer({ open, title, subtitle, description, onClose, children, footer, size = "md", closeOnBackdrop = true, className }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalLayer(open, panelRef, closeOnBackdrop ? onClose : undefined);
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-overlay animate-fade-in"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "flex h-full w-full flex-col border-l border-border bg-surface-raised text-fg shadow-lg outline-none animate-slide-in-right",
          drawerSize[size],
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-title text-fg">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 truncate font-mono text-body-sm text-fg-subtle">{subtitle}</p> : null}
            {description ? <p className="mt-1 text-body-sm text-fg-subtle">{description}</p> : null}
          </div>
          <IconButton icon={X} label="Fechar" size="sm" onClick={onClose} tooltip={false} className="-mr-1.5" />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
